import type { SupportTicket } from '@/api/supportApi'

/**
 * Pure rules for files, links and the similar-ticket hint on the Support
 * screen (vitest runs no .tsx). Smart Support enforces the same limits; these
 * only say so before anything is sent.
 */

export const MAX_FILE_BYTES = 25 * 1024 * 1024
export const MAX_FILES = 10

/** Mirrors Smart Support's blocklist: types that render or run in a browser. */
const BLOCKED = new Set(['text/html', 'application/xhtml+xml', 'application/javascript', 'text/javascript', 'image/svg+xml', 'application/x-msdownload'])

const bare = (type: string) => type.split(';')[0].trim().toLowerCase()

export interface FileLike {
  name: string
  type: string
  size: number
}

export function checkFiles<T extends FileLike>(already: FileLike[], incoming: T[]): { accepted: T[]; rejected: { name: string; reason: string }[] } {
  const accepted: T[] = []
  const rejected: { name: string; reason: string }[] = []
  for (const file of incoming) {
    if (BLOCKED.has(bare(file.type))) rejected.push({ name: file.name, reason: 'That type of file is not accepted' })
    else if (file.size > MAX_FILE_BYTES) rejected.push({ name: file.name, reason: 'Larger than 25 MB' })
    else if (already.length + accepted.length >= MAX_FILES) rejected.push({ name: file.name, reason: `At most ${MAX_FILES} files` })
    else accepted.push(file)
  }
  return { accepted, rejected }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`
}

export const isImage = (type: string) => bare(type).startsWith('image/')

export type PreviewKind = 'image' | 'pdf' | 'text' | 'audio' | 'video' | 'none'

/** How the viewer shows a file; mirrors Smart Support's isPreviewable (which has the last word). */
export function previewKind(type: string): PreviewKind {
  const t = bare(type)
  if (t === 'image/svg+xml') return 'none'
  if (t.startsWith('image/')) return 'image'
  if (t === 'application/pdf') return 'pdf'
  if (t === 'text/plain' || t === 'text/csv') return 'text'
  if (t.startsWith('audio/')) return 'audio'
  if (t.startsWith('video/')) return 'video'
  return 'none'
}

export type TextSegment = { text: string; href?: string }

const URL_RE = /https?:\/\/[^\s<>"']+/g

/** Text split into plain and http(s) link segments; trailing punctuation stays text. Never any other scheme. */
export function linkify(text: string): TextSegment[] {
  const out: TextSegment[] = []
  let last = 0
  for (const m of text.matchAll(URL_RE)) {
    let url = m[0]
    const trail = /[.,;:!?)\]]+$/.exec(url)?.[0] ?? ''
    url = url.slice(0, url.length - trail.length)
    const start = m.index ?? 0
    if (start > last) out.push({ text: text.slice(last, start) })
    out.push({ text: url, href: url })
    last = start + url.length
  }
  if (last < text.length) out.push({ text: text.slice(last) })
  return out.length ? out : [{ text }]
}

const STOP = new Set(['the', 'and', 'for', 'not', 'with', 'from', 'this', 'that', 'are', 'was', 'has', 'have', 'our', 'any', 'all', 'can', 'cannot', 'when', 'into', 'after', 'since', 'there', 'their', 'is'])

const words = (s: string) =>
  new Set(
    s
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length >= 3 && !STOP.has(w)),
  )

/** Up to three OPEN tickets sharing meaningful words with a subject being typed, most shared first. */
export function similarTickets(subject: string, tickets: Pick<SupportTicket, 'number' | 'key' | 'subject' | 'status'>[]): Pick<SupportTicket, 'number' | 'key' | 'subject' | 'status'>[] {
  const want = words(subject)
  if (!want.size) return []
  return tickets
    .filter((t) => t.status !== 'resolved' && t.status !== 'closed')
    .map((t) => ({ t, score: [...words(t.subject)].filter((w) => want.has(w)).length }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.t.number - a.t.number)
    .slice(0, 3)
    .map((x) => x.t)
}
